import * as moment from 'moment';

function fieldName(field) {
  return field.split(':')[1];
}

function fieldWhere(field) {
  return field.split(':')[0];
}

function dateFromObj(o) {
  return new Date(o.year, o.month, o.day);
}

function dateToObj(d) {
  return {
    year: d.getFullYear(),
    month: d.getMonth(),
    day: d.getDate(),
  };
}

/**
 * Splitted from metric.controller.js, to lighten it (~2800 lines before split)
 * We can probably do better when migrating in angular, for now it's not refactoring, just splitting.
 * Create could be in constructor, dependencies injection, extend abstract class, remove self, remove bind(this)...
 */
export class DayRangeSelector {
  // useless declarations in JS (not in TS), just to see class members at a glance
  company;
  metric;
  DateRangePickerDialogFactory;
  LocalStorageFactory;
  MetricsFactory;
  $filter;
  $translate;

  fields;
  key;
  minDate;
  self;

  constructor(company, metric, DateRangePickerDialogFactory, LocalStorageFactory, MetricsFactory, $filter, $translate) {
    this.company = company;
    this.metric = metric;
    this.DateRangePickerDialogFactory = DateRangePickerDialogFactory;
    this.LocalStorageFactory = LocalStorageFactory;
    this.MetricsFactory = MetricsFactory;
    this.$filter = $filter;
    this.$translate = $translate;
  }

  // submitQueryFn optionnal
  create(selector, submitQueryFn) {
    this.fields = selector.fields ? selector.fields : [`where:${selector.name}`];
    this.key = `3-company-${this.company.id}-metric-${this.metric.id}-field-${selector.name}`;
    this.minDate = this.MetricsFactory.getMinDate(selector.name);

    this.self = {
      fromDate: null,
      toDate: null,
      pickDateRange: this.pickDateRange.bind(this),
      setDefaultValue: this.setDefaultValue.bind(this),
      hasValue: this.hasValue.bind(this),
      selectedCriteriaForXls: this.selectedCriteriaForXls.bind(this),
      apply: submitQueryFn || this.apply.bind(this),
      name: this.$translate.instant(`metrics/${this.metric.internal_name}/${selector.name}`),
      isDisplayed: selector.is_default || selector.displayed_by_default,
      isMandatory: selector.is_default,
      type: selector.type,
      remove: this.remove.bind(this),
      load: this.load.bind(this),
      loadFromReport: this.loadFromReport.bind(this),
      save: this.save.bind(this),
      query: this.query.bind(this),
      toString: this.toString.bind(this),
    };
    return this.self;
  }

  pickDateRange($event) {
    this.DateRangePickerDialogFactory.show({
      fromDate: this.self.fromDate,
      toDate: this.self.toDate,
      minDate: this.minDate ? moment(this.minDate).toDate() : null,
      isMandatory: this.self.isMandatory,
      targetEvent: $event,
    }).then(result => {
      this.self.fromDate = result.fromDate;
      this.self.toDate = result.toDate;
      this.self.apply();
    });
  }

  setDefaultValue() {
    const from = new Date();
    from.setDate(from.getDate() - 7);
    this.self.fromDate = from;
    this.self.toDate = new Date();
  }

  hasValue() {
    return this.self.fromDate && this.self.toDate;
  }

  selectedCriteriaForXls() {
    return {
      name: this.self.name,
      values: [{ name: this.self.toString() }],
    };
  }

  apply() {
    this.submitQuery();
  }

  remove() {
    // #1243 - pascal - prevent removal of date range if it is set by default for this stat
    if (!this.self.isMandatory) {
      this.self.fromDate = null;
      this.self.toDate = null;
    }
  }

  load() {
    const value = this.LocalStorageFactory.getItem(this.key);
    if (value && value.value) {
      this.self.fromDate = dateFromObj(value.value.from);
      this.self.toDate = dateFromObj(value.value.to);
      this.self.isDisplayed = true;
    } else if (this.self.isMandatory) {
      this.self.setDefaultValue();
    }
  }

  loadFromReport(selectedReport, reportDate) {
    if (reportDate) {
      const filtersWhere = [];
      Object.keys(reportDate.filters).forEach(key => {
        filtersWhere.push(reportDate.filters[key]);
      });
      reportDate.filters = filtersWhere;

      for (const field of this.fields) {
        if (reportDate.filters?.length && fieldName(field) === reportDate.filters[0][0][0][0].lvalue) {
          this.self.fromDate = new Date(reportDate.start_date);
          this.self.toDate = new Date(reportDate.end_date);
          this.self.isDisplayed = true;
        }
      }
    }
  }

  save() {
    let toSave = null;
    if (this.self.hasValue()) {
      toSave = {
        from: dateToObj(this.self.fromDate),
        to: dateToObj(this.self.toDate),
      };
    }
    this.LocalStorageFactory.setItem(this.key, { value: toSave });
  }

  query() {
    if (!this.self.hasValue()) {
      return [];
    }
    const retval = {};
    for (const field of this.fields) {
      retval[fieldWhere(field)] = [
        [
          {
            op: '>=',
            lvalue: fieldName(field),
            rvalue: this.MetricsFactory.formatFromDateForDayRangeQuery(this.self.fromDate),
          },
          {
            op: '<=',
            lvalue: fieldName(field),
            rvalue: this.MetricsFactory.formatToDateForDayRangeQuery(this.self.toDate),
          },
        ],
      ];
    }
    return retval;
  }

  toString() {
    return this.self.hasValue()
      ? `${this.$filter('date')(this.self.fromDate)} - ${this.$filter('date')(this.self.toDate)}`
      : '';
  }

  submitQuery() {
    this.MetricsFactory.queriesWithThrottleTime$.next();
  }
}
